import { Card } from '@employ-engage/common-components';
import { formatMeasure, formatNumber } from '@employ-engage/data';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

/**
 * The few differences worth a look, and an honest account of how they were
 * found.
 *
 * Every wording rule here is deliberate and the panel is wrong without them:
 *
 *   - **Never the word "significant".** It reads as "proven" to everyone who
 *     is not a statistician, and at these sample sizes almost nothing is
 *     proven. "Stands out" and "worth a look" claim exactly as much as the
 *     data supports.
 *   - **Never "driver", "impact", "leads to" or "causes".** Nothing here is
 *     causal; these are differences between groups of people who answered the
 *     same survey.
 *   - **The comparison is inline on every row.** A figure without its
 *     comparison group, its base and its interval is how "16%" gets published
 *     when the truth is "60% of the cases where it applied".
 *   - **The search space is disclosed, permanently, and not behind a tooltip.**
 */

export type Highlight = {
  questionKey: string;
  questionLabel: string;
  segmentKey: string;
  segmentLabel: string;
  value: number;
  interval?: { low: number; high: number } | null;
  n: number;
  restValue: number;
  restN: number;
  difference: number;
  probabilityOfSuperiority?: number | null;
  isCensus: boolean;
  responseRate?: number | null;
  otherSegmentsFlagged: number;
};

export type HighlightsDisclosure = {
  comparisons: number;
  questionsScanned: number;
  segmentsScanned: number;
  q: number;
  expectedFalse: number;
  belowEffectFloor: number;
  refusedForSize: number;
};

export type SegmentLevelOption = {
  level: number;
  label: string;
  groups: number;
  comparableGroups: number;
};

export interface HighlightsPanelProps {
  highlights?: Highlight[] | null;
  disclosure?: HighlightsDisclosure | null;
  segmentLabel?: string;
  segmentLevel?: number | null;
  levels?: SegmentLevelOption[] | null;
  onSegmentLevelChange?: (level: number) => void;
  measure?: string;
  scoredQuestions?: number;
  unscoredQuestions?: number;
  loading?: boolean;
  error?: string | null;
  className?: string;
}


export function HighlightsPanel({
  highlights,
  disclosure,
  segmentLabel,
  segmentLevel,
  levels,
  onSegmentLevelChange,
  measure = 'topBox',
  scoredQuestions = 0,
  unscoredQuestions = 0,
  loading,
  error,
  className,
}: HighlightsPanelProps) {
  const { t } = useTranslation();

  // A mean is a score on the scale and a box measure is a percentage, so the
  // same number means different things and is never formatted the same way.
  const isMean = measure === 'mean';
  const format = (value: number) => formatMeasure(value, measure);
  const gap = (value: number) =>
    isMean
      ? t('{{points}} points', {
          points: formatNumber(Math.abs(value), 'decimal1'),
        })
      : t('{{points}} percentage points', {
          points: formatNumber(Math.abs(value)),
        });

  const choosable = (levels ?? []).filter((option) => option.groups > 1);

  const body = () => {
    if (loading) {
      return (
        <p className="text-sm text-light-secondaryText dark:text-dark-secondaryText">
          {t('Looking for differences worth a look…')}
        </p>
      );
    }

    if (error) {
      return <p className="text-sm text-light-error">{error}</p>;
    }

    if (!scoredQuestions) {
      return (
        <p className="text-sm text-light-secondaryText dark:text-dark-secondaryText">
          {t(
            'Nothing here yet. Score a question’s answers — say which mean ' +
              'agreement and which do not — and this panel can start comparing ' +
              'teams on it.'
          )}
        </p>
      );
    }

    if (!highlights?.length) {
      return (
        <p className="text-sm text-light-secondaryText dark:text-dark-secondaryText">
          {disclosure?.refusedForSize
            ? t(
                'No differences stood out. {{count}} groups were too small to ' +
                  'compare — a group needs about 20 answers before a ' +
                  'comparison could change a decision.',
                { count: disclosure.refusedForSize }
              )
            : t('No differences stood out. That is a result, not a gap.')}
        </p>
      );
    }

    return (
      <ul className="flex flex-col gap-3">
        {highlights.map((highlight) => {
          const below = highlight.difference < 0;
          return (
            <li
              key={`${highlight.questionKey}:${highlight.segmentKey}`}
              className="rounded border border-light-alternate p-3 dark:border-dark-alternate"
            >
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-medium text-light-primaryText dark:text-dark-primaryText">
                  {highlight.segmentLabel}
                </span>
                <span
                  className={classNames(
                    'text-xs font-medium',
                    below ? 'text-light-error' : 'text-light-primary'
                  )}
                >
                  {below
                    ? t('{{gap}} below the rest', {
                        gap: gap(highlight.difference),
                      })
                    : t('{{gap}} above the rest', {
                        gap: gap(highlight.difference),
                      })}
                </span>
              </div>

              <p className="mt-1 text-sm text-light-secondaryText dark:text-dark-secondaryText">
                {highlight.questionLabel}
              </p>

              {/* The whole comparison, so nothing has to be looked up
                  elsewhere and no figure appears without its base. */}
              <p className="mt-2 text-xs text-light-secondaryText dark:text-dark-secondaryText">
                {format(highlight.value)}
                {' · '}
                {t('{{count}} answered', { count: highlight.n })}
                {highlight.interval
                  ? ` · ${t('could be {{low}} to {{high}}', {
                      low: format(highlight.interval.low),
                      high: format(highlight.interval.high),
                    })}`
                  : ''}
                {highlight.isCensus && highlight.responseRate
                  ? ` · ${t('{{percent}}% of those invited answered', {
                      percent: Math.round(highlight.responseRate * 100),
                    })}`
                  : ''}
              </p>
              <p className="text-xs text-light-secondaryText dark:text-dark-secondaryText">
                {t('Everyone else: {{value}} from {{count}} answers', {
                  value: format(highlight.restValue),
                  count: highlight.restN,
                })}
              </p>

              {highlight.probabilityOfSuperiority !== null &&
                highlight.probabilityOfSuperiority !== undefined && (
                  <p className="mt-1 text-xs text-light-secondaryText dark:text-dark-secondaryText">
                    {t(
                      'Pick one person from each group at random: {{percent}}% ' +
                        'of the time the {{segment}} person rates it lower.',
                      {
                        percent: Math.round(
                          highlight.probabilityOfSuperiority * 100
                        ),
                        segment: highlight.segmentLabel,
                      }
                    )}
                  </p>
                )}

              {highlight.otherSegmentsFlagged > 0 && (
                <p className="mt-1 text-xs italic text-light-secondaryText dark:text-dark-secondaryText">
                  {t(
                    '{{count}} other groups also differ on this question, ' +
                      'largely because this one moves the comparison.',
                    { count: highlight.otherSegmentsFlagged }
                  )}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <Card className={className}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold text-light-primaryText dark:text-dark-primaryText">
          {t('Worth a look')}
        </h2>
        {/* Only the levels that split into more than one group. The top of
            any org tree is the company itself, and "the company against the
            rest of the company" is not a comparison anyone can make. */}
        {choosable.length > 1 && onSegmentLevelChange ? (
          <label className="flex items-center gap-2 text-xs text-light-secondaryText dark:text-dark-secondaryText">
            {t('by')}
            <select
              className="rounded border border-light-alternate bg-transparent px-2 py-1 text-xs dark:border-dark-alternate"
              value={segmentLevel ?? ''}
              onChange={(e) => onSegmentLevelChange(Number(e.target.value))}
            >
              {choosable.map((option) => (
                <option key={option.level} value={option.level}>
                  {option.comparableGroups >= 2
                    ? t('{{label}} ({{count}} groups)', {
                        label: option.label,
                        count: option.groups,
                      })
                    : t('{{label}} ({{count}} groups, too small)', {
                        label: option.label,
                        count: option.groups,
                      })}
                </option>
              ))}
            </select>
          </label>
        ) : (
          segmentLabel && (
            <span className="text-xs text-light-secondaryText dark:text-dark-secondaryText">
              {t('by {{field}}', { field: segmentLabel })}
            </span>
          )
        )}
      </div>

      <div className="mt-3">{body()}</div>

      {/* Permanent, and not behind a tooltip: a highlight means nothing
          without the size of the search it was picked out of. */}
      {!!disclosure?.comparisons && (
        <p className="mt-4 border-t border-light-alternate pt-3 text-xs text-light-secondaryText dark:border-dark-alternate dark:text-dark-secondaryText">
          {t(
            'We compared {{questions}} questions across {{segments}} groups — ' +
              '{{comparisons}} comparisons in all.',
            {
              questions: disclosure.questionsScanned,
              segments: disclosure.segmentsScanned,
              comparisons: disclosure.comparisons,
            }
          )}{' '}
          {highlights?.length
            ? t(
                'About {{expected}} of the {{shown}} shown will be chance. ' +
                  'These are places to look, not conclusions.',
                {
                  expected: disclosure.expectedFalse,
                  shown: highlights.length,
                }
              )
            : ''}
          {unscoredQuestions > 0
            ? ` ${t(
                '{{count}} questions are not scored yet, so they were not compared.',
                {
                  count: unscoredQuestions,
                }
              )}`
            : ''}
        </p>
      )}
    </Card>
  );
}

export default HighlightsPanel;
